import { fetchEvent } from "api"
import {
    FetchEventResponse,
    normalizeEventPayload,
    normalizeEventTimeSlots,
} from "api/event/fetchEvent"
import { useQuery } from "@tanstack/react-query"
import { useRouter } from "next/router"
import { useEffect, useMemo } from "react"
import { queryKeys } from "utils/queryKeys"
import { resolveEventRouteId } from "utils/eventPage"

export function useResolvedEventId(): string {
    const router = useRouter()
    const rawEventId = router.query.eventid
    const eventId =
        typeof rawEventId === "string" ? resolveEventRouteId(rawEventId) : ""

    return eventId
}

export function useEventPageQuery() {
    const router = useRouter()
    const eventId = useResolvedEventId()

    const query = useQuery<FetchEventResponse>({
        queryKey: queryKeys.event(eventId),
        queryFn: () => fetchEvent(eventId),
        enabled: !!eventId,
        staleTime: 300000,
        gcTime: 600000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
            return error.message !== "EVENT_NOT_FOUND" && failureCount < 3
        },
    })

    useEffect(() => {
        if (query.isError && query.error) {
            console.error("Error fetching event data:", query.error)
        }
    }, [query.isError, query.error])

    useEffect(() => {
        if (query.error instanceof Error && query.error.message === "EVENT_NOT_FOUND") {
            router.push("/404")
        }
    }, [query.error, router])

    const event = useMemo(
        () => normalizeEventPayload(query.data?.payload),
        [query.data?.payload]
    )

    const bookingTimeSlots = useMemo(
        () => normalizeEventTimeSlots(event),
        [event]
    )

    return {
        eventId,
        event,
        bookingTimeSlots,
        ...query,
    }
}
